import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { firebase_auth } from "@src/utility_functions/database_operations/config/firebaseConfig";
import { FederatedLoginButton } from "@src/components/Buttons/FederatedLoginButton";
import { OrSectionSeparator } from "@src/components/Forms/FormElements/OrSectionSeparator";
import { TextInput } from "@src/components/Forms/FormElements/Inputs/TextInput";
import { PasswordInput } from "@src/components/Forms/FormElements/Inputs/PasswordInput";
import { RegistrationForm } from "@src/components/Forms/User/createUser/RegistrationForm";
import { login_with_google } from "@src/utility_functions/database_operations/users/federated_logins/google/login_with_google.js";
import { signInWithEmailAndPassword } from "firebase/auth";
import { validateInput } from "@src/utility_functions/inputValidation/validateInput";
import {
  validateEmail,
  validatePassword,
} from "#shared/inputValidation/inputValidation.js";
import { ForgotPasswordLink } from "@src/components/Forms/FormElements/ForgotPassword/ForgotPasswordLink.tsx";
import type { InputValidationError } from "@src/types/timeline.ts";
import { useModal } from "@src/utility_functions/customHooks/useModal";

export const LoginForm = (props) => {
  const { closeDialog, setModalContent } = useModal();
  const queryClient = useQueryClient();
  const [emailError, setEmailError] = useState<InputValidationError>({
    errorClass: "",
    errorMessages: [],
  });
  const [passwordError, setPasswordError] = useState<InputValidationError>({
    errorClass: "",
    errorMessages: [],
  });
  const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const formData = new FormData(event.currentTarget);
    const formDataEntries = Object.fromEntries(formData.entries());
    const emailValid =
      validateEmail(formDataEntries.email).errorClass === "valid";
    const passwordValid =
      validatePassword(formDataEntries.password).errorClass === "valid";
    const formValid = emailValid && passwordValid;

    if (formValid) {
      try {
        await signInWithEmailAndPassword(
          firebase_auth,
          String(formDataEntries.email),
          String(formDataEntries.password),
        );
        localStorage.setItem("loginMethod", "password");
        closeDialog();
      } catch (error) {
        if (
          typeof error === "object" &&
          error !== null &&
          "code" in error &&
          typeof (error as any).code === "string"
        ) {
          const errorCodeSplit = (error as any).code.split("/")[1];
          if (errorCodeSplit === "user-not-found") {
            setEmailError({
              errorClass: "semi_valid",
              errorMessages: ["User not found"],
            });
          } else if (errorCodeSplit === "wrong-password") {
            setPasswordError({
              errorClass: "semi_valid",
              errorMessages: ["Wrong password"],
            });
          } else if (errorCodeSplit === "invalid-credential") {
            setEmailError({
              errorClass: "semi_valid",
              errorMessages: ["Invalid credentials"],
            });
            setPasswordError({
              errorClass: "semi_valid",
              errorMessages: ["Invalid credentials"],
            });
          }
        }
      }
    } else {
      validateInput(formDataEntries.email, validateEmail, setEmailError);
      validateInput(
        formDataEntries.password,
        validatePassword,
        setPasswordError,
      );
    }
  };

  return (
    <form onSubmit={submitHandler} noValidate={true}>
      <FederatedLoginButton
        logo="/images/logos/google_login_button_icon.jpg"
        text="Login with Google"
        size="large"
        custom_class="_margin-bottom--1rem"
        handleClick={async () => {
          const serverResponse = await login_with_google();
          queryClient.invalidateQueries({
            queryKey: ["userDocument", serverResponse.id, serverResponse.id],
          });
          closeDialog();
        }}
      />
      <OrSectionSeparator
        className="_margin-top--1rem _margin-bottom--1rem"
        case="lower"
      />
      <TextInput
        inputId="loginFormEmailInput"
        inputType="email"
        name="email"
        labelText="Email"
        maxlength={50}
        defaultValue=""
        placeholder="Email"
        autocomplete="email"
        required={true}
        validationResponse={emailError}
        onChangeFunction={(event: React.FocusEvent<HTMLInputElement>) => {
          validateInput(event.target.value, validateEmail, setEmailError);
        }}
      />
      <PasswordInput
        className="_margin-top--1rem"
        inputId="loginFormPasswordInput"
        inputType="password"
        name="password"
        labelText="Password"
        maxlength={50}
        defaultValue=""
        placeholder="Password"
        autocomplete="new-password"
        required={true}
        validationResponse={passwordError}
        onChange={(event: React.FocusEvent<HTMLInputElement>) => {
          validateInput(event.target.value, validatePassword, setPasswordError);
        }}
      />
      <ForgotPasswordLink modalOpen={true} />
      <button
        className="btn btn--primary btn--block _margin-top--1rem"
        type="submit"
      >
        Log In
      </button>

      <hr className="_margin-top--1rem _margin-bottom--1rem" />
      <p className="_font-size--14px">
        Don&apos;t have an account?&nbsp;
        <button
          style={{ color: "var(--blue-30)", fontSize: "14px" }}
          onClick={() => {
            setModalContent({
              modalTitle: "Create Account",
              dialogWidth: "50%",
              modalContent: (
                <div>
                  <RegistrationForm loggedInUser={props.loggedInUser} />
                </div>
              ),
            });
          }}
        >
          Create Account
        </button>
      </p>
    </form>
  );
};
